<template>
  <div
    data-testid="matchmaking-container"
    class="flex flex-col p-4 md:p-6 lg:p-8 min-h-screen w-full mx-auto safe-area-insets"
  >
    <!-- Homepage State — single flex column that fits the viewport (no scroll on
         mobile); masthead + arena card centered as one unit. The global settings
         FAB (GameApp) covers settings/profile, so this screen has no gear of its
         own. -->
    <section v-if="currentState === 'homepage'" class="hm-col safe-area-insets">
      <div class="hm-center">
        <!-- brand masthead — glowing wordmark + tagline + VS hairline -->
        <header class="hm-mast" aria-labelledby="main-title">
          <h1 id="main-title" class="hm-wordmark font-display">
            {{ STRINGS.branding.title }}
          </h1>
          <p class="hm-tagline">{{ STRINGS.branding.subtitle }}</p>
          <div class="hm-vs" aria-hidden="true">
            <span class="hm-vs-line hm-vs-line--l"></span>
            <span class="hm-vs-text font-display">VS</span>
            <span class="hm-vs-line hm-vs-line--r"></span>
          </div>
        </header>

        <!-- arena card — player identity + the two calls to action -->
        <div class="hm-card" aria-labelledby="main-content-heading">
          <h2 id="main-content-heading" class="sr-only">
            {{ STRINGS.aria.descriptions.gameIntroductionHeading }}
          </h2>

          <!-- player identity row -->
          <div class="hm-player">
            <div class="hm-avatar font-mono" aria-hidden="true">{{ playerInitial }}</div>
            <div class="hm-player-meta">
              <p class="hm-name font-mono" data-testid="auth-player-name">
                {{ auth.user.value?.name }}
              </p>
              <span class="hm-player-status">
                <span
                  class="hm-dot"
                  :class="{ 'hm-dot--warn': !auth.isEmailVerified.value }"
                ></span>
                {{ auth.isEmailVerified.value ? 'Ready' : 'Verify email' }}
              </span>
            </div>
          </div>

          <div class="hm-divider"></div>

          <!-- Email verification banner -->
          <div
            v-if="!auth.isEmailVerified.value"
            class="mb-1 p-3 rounded-lg border border-energy-warning/30 bg-energy-warning/5 text-sm text-arena-text-muted text-center"
            role="status"
            data-testid="email-verification-banner"
          >
            <p class="m-0 mb-2">Check your email to verify your account</p>
            <p
              v-if="resendFeedback"
              class="m-0 mb-2 text-xs"
              :class="resendSuccess ? 'text-green-400' : 'text-energy-warning'"
              data-testid="resend-feedback"
            >
              {{ resendFeedback }}
            </p>
            <button
              class="text-xs underline text-arena-text-muted hover:text-arena-text-bright transition-colors bg-transparent border-none cursor-pointer p-0"
              :disabled="resendingEmail"
              data-testid="resend-verification-button"
              @click="handleResendVerification"
            >
              {{ resendingEmail ? 'Sending...' : 'Resend verification email' }}
            </button>
          </div>

          <!-- action stack -->
          <div class="hm-actions">
            <button
              type="button"
              class="mm-btn mm-btn--primary"
              @click="handlePlayNow"
              :disabled="!auth.isEmailVerified.value"
              :aria-label="STRINGS.aria.labels.startFindingOpponent"
              data-testid="play-now-button"
            >
              <AppIcon name="word-search" :size="18" class="mm-btn-icon" />
              {{ STRINGS.ui.buttons.playNow }}
            </button>

            <button
              type="button"
              class="mm-btn mm-btn--secondary"
              @click="showBotDifficulty = !showBotDifficulty"
              :aria-label="STRINGS.aria.labels.startBotGame"
              :aria-expanded="showBotDifficulty"
              data-testid="play-bot-button"
            >
              <AppIcon name="bot" :size="18" class="mm-btn-icon" />
              {{ STRINGS.ui.buttons.vsBot }}
            </button>

            <p v-if="!auth.isEmailVerified.value" class="hm-bot-note">
              3 bot games per hour while unverified
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Searching State (Direction A — Quiet Pulse) -->
    <main
      v-if="currentState === 'searching' || currentState === 'extended-search'"
      class="flex-1 flex flex-col items-center justify-center min-h-screen p-4 md:p-6 lg:p-8"
    >
      <section
        role="status"
        aria-live="polite"
        aria-labelledby="searching-heading"
        class="mm-stack"
      >
        <div class="mmA-pulse" aria-hidden="true">
          <span class="ring"></span>
          <span class="ring"></span>
          <span class="ring"></span>
          <span class="core"><AppIcon name="word-search" :size="26" /></span>
        </div>

        <div class="mm-headstack">
          <p class="mm-eyebrow">{{ STRINGS.status.search.matchmakingEyebrow }}</p>
          <h2 id="searching-heading" class="mm-title queue-status">
            {{ STRINGS.status.game.findingOpponent }}
          </h2>
          <p class="mm-status">{{ STRINGS.status.search.searchingStatus }}</p>
        </div>

        <div class="mm-actions">
          <button
            type="button"
            class="mm-btn mm-btn--secondary"
            @click="showBotDifficulty = !showBotDifficulty"
            :aria-label="STRINGS.aria.labels.startBotGame"
            :aria-expanded="showBotDifficulty"
            data-testid="search-play-bot-button"
          >
            {{ STRINGS.ui.buttons.playBotInstead }}
          </button>
          <button
            type="button"
            class="mm-btn mm-btn--ghost"
            @click="cancelSearch"
            :aria-label="STRINGS.aria.labels.cancelSearch"
          >
            {{ STRINGS.ui.buttons.cancel }}
          </button>
        </div>
      </section>
    </main>

    <!-- Bot Start State (Direction A — Quiet Pulse, no "Finding Opponent") -->
    <main
      v-if="currentState === 'starting-bot'"
      class="flex-1 flex flex-col items-center justify-center min-h-screen p-4 md:p-6 lg:p-8"
      data-testid="starting-bot-section"
    >
      <section
        role="status"
        aria-live="polite"
        aria-labelledby="starting-bot-heading"
        class="mm-stack"
      >
        <div class="mmA-pulse" aria-hidden="true">
          <span class="ring"></span>
          <span class="ring"></span>
          <span class="ring"></span>
          <span class="core"><AppIcon name="bot" :size="26" /></span>
        </div>

        <div class="mm-headstack">
          <p class="mm-eyebrow">{{ STRINGS.status.search.startingBotEyebrow }}</p>
          <h2 id="starting-bot-heading" class="mm-title queue-status">
            {{ STRINGS.status.search.startingBotTitle }}
          </h2>
          <p class="mm-status">{{ STRINGS.status.search.startingBotStatus }}</p>
        </div>

        <div class="mm-actions">
          <button
            type="button"
            class="mm-btn mm-btn--ghost"
            @click="cancelSearch"
            :aria-label="STRINGS.aria.labels.cancelSearch"
          >
            {{ STRINGS.ui.buttons.cancel }}
          </button>
        </div>
      </section>
    </main>

    <!-- Match Found State (Direction A — Quiet Pulse, found) -->
    <main
      v-if="currentState === 'match-found'"
      class="flex-1 flex flex-col items-center justify-center min-h-screen p-4 md:p-6 lg:p-8"
      data-testid="match-found-section"
    >
      <section
        role="status"
        aria-labelledby="match-found-heading"
        aria-describedby="match-found-desc"
        class="mm-stack"
      >
        <div
          class="mmA-pulse is-found"
          role="img"
          :aria-label="STRINGS.aria.labels.successOpponentFound"
        >
          <span class="ring"></span>
          <span class="ring"></span>
          <span class="ring"></span>
          <span class="core"><AppIcon name="checkmark" :size="30" /></span>
        </div>

        <div class="mm-headstack">
          <p class="mm-eyebrow is-found">{{ STRINGS.status.game.matchFoundEyebrow }}</p>
          <h2 id="match-found-heading" class="mm-title queue-status">
            {{ STRINGS.status.game.getReady }}
          </h2>
          <p
            id="match-found-desc"
            class="mm-status"
            aria-live="polite"
            aria-atomic="true"
            data-testid="countdown-display"
          >
            {{ STRINGS.templates.startingInSeconds(countdown) }}
          </p>
        </div>

        <div class="mm-actions">
          <button
            type="button"
            class="mm-btn mm-btn--primary"
            disabled
            :aria-label="STRINGS.aria.labels.connectingToGame"
          >
            {{ STRINGS.ui.buttons.connecting }}
          </button>
        </div>
      </section>
    </main>

    <!-- No-Opponents State (Direction A — Quiet Pulse, failure) -->
    <main
      v-if="currentState === 'timeout-error'"
      class="flex-1 flex flex-col items-center justify-center min-h-screen p-4 md:p-6 lg:p-8"
    >
      <section role="alert" aria-labelledby="timeout-error-heading" class="mm-stack">
        <div class="mmA-pulse is-empty" aria-hidden="true">
          <span class="core"><AppIcon name="bot" :size="26" /></span>
        </div>

        <div class="mm-headstack">
          <p class="mm-eyebrow is-empty">{{ STRINGS.errors.timeout.eyebrow }}</p>
          <h2 id="timeout-error-heading" class="mm-title queue-status">
            {{ STRINGS.errors.timeout.title }}
          </h2>
          <p class="mm-status">{{ STRINGS.errors.timeout.message }}</p>
        </div>

        <div class="mm-actions">
          <button
            type="button"
            class="mm-btn mm-btn--primary"
            @click="startMatchmaking"
            :aria-label="STRINGS.aria.labels.tryAgainSearch"
          >
            <AppIcon name="refresh" :size="17" class="mm-btn-icon" />
            {{ STRINGS.ui.buttons.searchAgain }}
          </button>
          <button
            type="button"
            class="mm-btn mm-btn--secondary"
            @click="showBotDifficulty = !showBotDifficulty"
            :aria-label="STRINGS.aria.labels.startBotGame"
            :aria-expanded="showBotDifficulty"
            data-testid="timeout-play-bot-button"
          >
            {{ STRINGS.ui.buttons.playBotInstead }}
          </button>
          <button
            type="button"
            class="mm-btn mm-btn--ghost"
            @click="goHome"
            :aria-label="STRINGS.aria.labels.returnHome"
          >
            {{ STRINGS.ui.buttons.backToHome }}
          </button>
        </div>
      </section>
    </main>

    <!-- Connection Error State (Direction A — Quiet Pulse, reconnecting) -->
    <main
      v-if="currentState === 'connection-error'"
      class="flex-1 flex flex-col items-center justify-center min-h-screen p-4 md:p-6 lg:p-8"
    >
      <section
        role="alert"
        aria-labelledby="connection-error-heading"
        aria-describedby="connection-error-desc"
        class="mm-stack"
      >
        <div
          class="mmA-pulse is-error is-reconnecting"
          role="img"
          :aria-label="STRINGS.aria.labels.alertConnectionLost"
        >
          <span class="ring"></span>
          <span class="ring"></span>
          <span class="ring"></span>
          <span class="core"><AppIcon name="warning" :size="28" /></span>
        </div>

        <div class="mm-headstack">
          <p class="mm-eyebrow is-error">{{ STRINGS.errors.connection.connectionLost }}</p>
          <h2 id="connection-error-heading" class="mm-title queue-status">
            {{ STRINGS.errors.connection.reconnectingTitle }}
          </h2>
          <p id="connection-error-desc" class="mm-status" aria-live="assertive" aria-atomic="true">
            {{ STRINGS.errors.connection.reconnectingDetail }}
          </p>
        </div>

        <div class="mm-actions">
          <button
            type="button"
            class="mm-btn mm-btn--primary"
            @click="retryConnection"
            :aria-label="STRINGS.aria.labels.retryConnection"
          >
            <AppIcon name="refresh" :size="17" class="mm-btn-icon" />
            {{ STRINGS.ui.buttons.retryNow }}
          </button>
          <button
            type="button"
            class="mm-btn mm-btn--ghost"
            @click="goHome"
            :aria-label="STRINGS.aria.labels.returnHome"
          >
            {{ STRINGS.ui.buttons.backToHome }}
          </button>
        </div>
      </section>
    </main>

    <DifficultySelector
      :visible="showBotDifficulty"
      @select="handleDifficultySelect"
      @close="showBotDifficulty = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useTutorialState } from '@/composables/useTutorialState';
import AppIcon from './ui/icons/AppIcon.vue';
import DifficultySelector from './DifficultySelector.vue';
import { useWebSocket } from '@/composables/useWebSocket';
import { useAuth } from '@/composables/useAuth';
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts';
import { useLiveAnnouncements } from '@/composables/useLiveAnnouncements';
import { useFocusManagement } from '@/composables/useFocusManagement';
import { GAME_CONFIG, ENV_CONFIG } from '@/config/constants';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';
import type { MatchmakingScreenState } from '../types/websocket';
import type { ClientMessage, Difficulty } from '@/types/generated';
import type { MatchFoundInfo } from '@/composables/useGameFlow';
import STRINGS from '@/constants/strings';

interface Props {
  websocketUrl?: string;
  autoStart?: boolean;
  // When set, a match has been found at the app level: show the Direction A
  // "found" screen instead of unmounting in favour of a separate face-off view.
  matchFoundInfo?: MatchFoundInfo | null;
  // True while the settings overlay is open. Opening settings while actively
  // searching cancels matchmaking so the player isn't pulled into a game while
  // distracted in settings (issue #295).
  settingsOpen?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  websocketUrl: ENV_CONFIG.getWebSocketUrl(),
  autoStart: false,
  matchFoundInfo: null,
  settingsOpen: false,
});

// WebSocket composable. autoAcquire is off so this screen does not pin the
// connection open while it is merely mounted: the homepage needs no socket, and
// keeping one open there leaks it across idle periods (#299). Instead we hold a
// reference only while off the homepage (active search / match-found), via the
// currentState watcher below.
const { connect, sendMessage, connectionStatus, lastError, acquire, release } = useWebSocket({
  url: props.websocketUrl,
  autoAcquire: false,
});

// Keyboard shortcuts composable
const keyboardShortcuts = useKeyboardShortcuts({ enableDefaultShortcuts: true });

// Live announcements composable
const liveAnnouncements = useLiveAnnouncements();

// Focus management composable
const focusManagement = useFocusManagement();

// Auth composable
const auth = useAuth();

// Resend verification state
const resendingEmail = ref(false);
const resendFeedback = ref('');
const resendSuccess = ref(false);

// Reactive State
const currentState = ref<MatchmakingScreenState>('homepage');
const countdown = ref(3);
const showBotDifficulty = ref(false);

// Connection lifetime: hold a WebSocket reference for any currentState other
// than 'homepage', and release it on 'homepage' — that single rule (not an
// allow-list of states) is what the watcher below implements, so any future
// off-homepage state is covered automatically. Balanced so repeated watcher
// fires can't unbalance the refcount. Combined with GameApp owning the gameplay
// phases, the manager auto-disconnects once nothing holds a reference — so an
// idle homepage keeps no open socket (#299).
let holdsConnection = false;
const acquireConnection = () => {
  if (!holdsConnection) {
    acquire();
    holdsConnection = true;
  }
};
const releaseConnection = () => {
  if (holdsConnection) {
    release();
    holdsConnection = false;
  }
};

watch(
  currentState,
  state => {
    if (state === 'homepage') {
      releaseConnection();
    } else {
      acquireConnection();
    }
  },
  { immediate: true }
);

onUnmounted(releaseConnection);

// The match-found countdown ticks down each second so the "Starting in N
// seconds…" copy stays honest (it used to be set once and freeze).
// countdownDurationMs is the server's delay before GameStarted, and the screen
// unmounts when that arrives — so the count reaching 1 lines up with the game
// starting. Floored at 1: we never display "0 seconds".
let countdownIntervalId: ReturnType<typeof setInterval> | null = null;

const stopCountdown = () => {
  if (countdownIntervalId !== null) {
    clearInterval(countdownIntervalId);
    countdownIntervalId = null;
  }
};

const startCountdown = (durationMs: number) => {
  stopCountdown();
  countdown.value = Math.max(1, Math.ceil(durationMs / 1000));
  countdownIntervalId = setInterval(() => {
    if (countdown.value <= 1) {
      stopCountdown();
      return;
    }
    countdown.value -= 1;
  }, 1000);
};

// Match-found is owned by the app-level game flow (useGameFlow): when GameApp
// flips gameState to 'match-found' it keeps this component mounted and hands us
// the match info. Mirror that into our own state machine so the searching orb
// transitions straight into the Direction A "found" screen.
watch(
  () => props.matchFoundInfo,
  info => {
    if (info) {
      startCountdown(info.countdownDurationMs);
      currentState.value = 'match-found';
    } else if (currentState.value === 'match-found') {
      // App flow left match-found without unmounting us (e.g. a reset); fall
      // back to the homepage rather than stranding the found screen.
      stopCountdown();
      currentState.value = 'homepage';
    }
  },
  { immediate: true }
);

onUnmounted(stopCountdown);

// Computed Properties
const effectivePlayerName = computed(() => auth.user.value?.name?.trim() ?? '');

// First letter of the player's name for the letterpress avatar tile.
const playerInitial = computed(() => auth.user.value?.name?.charAt(0).toUpperCase() ?? '');

// Tutorial gate — shared by Play Now and VS Bot
const tutorial = useTutorialState();
let pendingAction: (() => void) | null = null;

function withTutorial(action: () => void) {
  // First-time users go straight to the briefing (covers all rules)
  if (!tutorial.hasSeen.value) {
    pendingAction = action;
    tutorial.openBriefing();
    return;
  }
  // Returning users: show rule change modal only when rules actually changed
  if (!tutorial.hasAcknowledgedRules.value) {
    pendingAction = action;
    tutorial.openRuleChange();
    return;
  }
  action();
}

function handlePlayNow() {
  withTutorial(startMatchmaking);
}

watch(tutorial.ruleChangeOpen, isOpen => {
  if (!isOpen && pendingAction) {
    // "See Full Rules" opened the codex — cancel the game-start flow
    if (tutorial.codexOpen.value) {
      pendingAction = null;
      return;
    }
    const action = pendingAction;
    pendingAction = null;
    action();
  }
});

watch(tutorial.briefingOpen, isOpen => {
  if (!isOpen && pendingAction) {
    // Briefing currently has no dismiss-without-completing path, so pendingAction
    // is always valid here. If a close/cancel button is added, this needs a guard
    // similar to the ruleChangeOpen watcher above.
    const action = pendingAction;
    pendingAction = null;
    action();
  }
});

// State Management Methods

// Shared connection core: connect and send message.
// Returns true if the message was sent successfully.
// `entryState` is the screen shown while connecting: human matchmaking uses
// 'searching' ("Finding Opponent"); bot games pass 'starting-bot' so the search
// copy never flickers for a match that was never a search.
const startGame = async (
  message: ClientMessage,
  entryState: MatchmakingScreenState = 'searching'
): Promise<boolean> => {
  currentState.value = entryState;

  try {
    await connect();
    if (!sendMessage(message)) {
      currentState.value = 'connection-error';
      return false;
    }
    return true;
  } catch {
    currentState.value = 'connection-error';
    return false;
  }
};

const startMatchmaking = async () => {
  if (
    !(await startGame({
      FindMatch: {
        player_name: effectivePlayerName.value,
      },
    }))
  ) {
    return;
  }

  setTimeout(() => {
    if (currentState.value === 'searching') {
      currentState.value = 'extended-search';
    }
  }, GAME_CONFIG.EXTENDED_SEARCH_TIMEOUT);

  setTimeout(() => {
    if (currentState.value === 'searching' || currentState.value === 'extended-search') {
      currentState.value = 'timeout-error';
    }
  }, GAME_CONFIG.MAX_SEARCH_TIMEOUT);
};

const cancelSearch = () => {
  // Leave the server-side matchmaking queue so a later OpponentFound can't pull
  // this player into a game after they've cancelled. Best-effort: the WebSocket
  // may already be gone, in which case the disconnect cleanup handles removal.
  sendMessage({ CancelMatchmaking: {} });
  goHome();
};

// Opening the settings overlay while actively searching cancels matchmaking
// (issue #295): the player has navigated away from the search UI, so keeping
// them queued would jarringly drop them into a game while they're in settings.
watch(
  () => props.settingsOpen,
  open => {
    if (open && (currentState.value === 'searching' || currentState.value === 'extended-search')) {
      cancelSearch();
    }
  }
);

// Honor autoStart even when it flips true *after* mount. onMounted only consumes
// it once, but a failed reconnect on reload sets autoStartMatchmaking while this
// screen is already mounted on the homepage (gameState never left 'matchmaking'),
// so without this watcher the intended recovery search never fires and the user
// is stranded. Guarded to the homepage so it can't hijack an active match-found.
// See #299.
watch(
  () => props.autoStart,
  started => {
    if (started && currentState.value === 'homepage') {
      startMatchmaking();
    }
  }
);

const handleDifficultySelect = (difficulty: Difficulty) => {
  showBotDifficulty.value = false;
  if (currentState.value === 'searching' || currentState.value === 'extended-search') {
    requestBotGameDuringSearch(difficulty);
  } else if (currentState.value === 'homepage') {
    withTutorial(() => startBotGame(difficulty));
  } else {
    startBotGame(difficulty);
  }
};

// Safety net for the 'starting-bot' screen. A bot match is created server-side
// almost instantly (the matchFoundInfo watch flips us to 'match-found'), and the
// human search-timeout guards deliberately do NOT fire in this state. So if the
// server never confirms the match, nothing would move us off "Setting up your
// game" — surface a connection error (and a telemetry signal) instead of
// hanging. Guarded on 'starting-bot' so it lapses harmlessly once matched.
const armBotStartTimeout = () => {
  setTimeout(() => {
    if (currentState.value === 'starting-bot') {
      reportClientError(
        new Error('Bot match was not confirmed within BOT_START_TIMEOUT'),
        ErrorLocation.MatchmakingBotStartTimeout
      );
      currentState.value = 'connection-error';
    }
  }, GAME_CONFIG.BOT_START_TIMEOUT);
};

const startBotGame = async (difficulty: Difficulty) => {
  // 'starting-bot' (not 'searching') so the "Finding Opponent" copy never shows
  // for a bot game; the matchFoundInfo watch flips us to 'match-found' shortly.
  const started = await startGame(
    { RequestBotGame: { difficulty, player_name: effectivePlayerName.value } },
    'starting-bot'
  );
  if (started) {
    armBotStartTimeout();
  }
};

const requestBotGameDuringSearch = (difficulty: Difficulty) => {
  showBotDifficulty.value = false;
  const sent = sendMessage({
    RequestBotGame: { difficulty, player_name: effectivePlayerName.value },
  });
  if (!sent) {
    currentState.value = 'connection-error';
    return;
  }
  // Switch off the "Finding Opponent" search copy now that this is a bot game.
  // This also lets the pending search-timeout guards (which only fire while
  // 'searching'/'extended-search') lapse so no spurious timeout-error appears.
  currentState.value = 'starting-bot';
  armBotStartTimeout();
};

const retryConnection = () => {
  startMatchmaking();
};

const goHome = () => {
  currentState.value = 'homepage';
};

const handleResendVerification = async () => {
  resendingEmail.value = true;
  resendFeedback.value = '';
  resendSuccess.value = false;
  try {
    await auth.resendVerification();
    resendSuccess.value = true;
    resendFeedback.value = 'Verification email sent! Check your inbox.';
  } catch (err: unknown) {
    if (err && typeof err === 'object' && 'type' in err) {
      const authErr = err as { type: string; message: string; retryAfterSeconds?: number };
      if (authErr.type === 'rate_limited') {
        if (authErr.retryAfterSeconds != null) {
          const minutes = Math.ceil(authErr.retryAfterSeconds / 60);
          resendFeedback.value = `Please wait ${minutes} minute${minutes !== 1 ? 's' : ''} before requesting another email.`;
        } else {
          resendFeedback.value =
            'Too many requests. Please wait a few minutes before trying again.';
        }
      } else {
        resendFeedback.value = authErr.message;
      }
    } else {
      resendFeedback.value = 'Failed to send. Please try again.';
    }
  } finally {
    resendingEmail.value = false;
  }
};

// Watch for connection status changes; auto-retry matchmaking on reconnection
watch([connectionStatus, lastError], ([status, error]) => {
  if (status === 'connected' && currentState.value === 'connection-error') {
    startMatchmaking();
  } else if (
    status === 'error' &&
    error &&
    (currentState.value === 'searching' ||
      currentState.value === 'extended-search' ||
      currentState.value === 'starting-bot')
  ) {
    currentState.value = 'connection-error';
  }
});

// Watch for game state changes and announce them
watch(currentState, (newState, oldState) => {
  showBotDifficulty.value = false;
  if (oldState && newState !== oldState) {
    switch (newState) {
      case 'searching':
        liveAnnouncements.announceGameState(
          STRINGS.status.search.searchingAnnouncement,
          STRINGS.status.search.searchingDetail
        );
        break;
      case 'extended-search':
        liveAnnouncements.announceGameState(
          STRINGS.status.search.stillSearching,
          STRINGS.status.search.stillSearchingDetail
        );
        break;
      case 'starting-bot':
        liveAnnouncements.announceGameState(
          STRINGS.status.search.startingBotAnnouncement,
          STRINGS.status.search.startingBotDetail
        );
        break;
      case 'match-found':
        liveAnnouncements.announceSuccess(STRINGS.status.search.opponentFoundAnnouncement);
        break;
      case 'timeout-error':
        liveAnnouncements.announceError(STRINGS.status.search.noOpponentFound);
        break;
      case 'connection-error':
        liveAnnouncements.announceError(STRINGS.status.connection.connectionLostCheck);
        break;
      case 'homepage':
        if (oldState !== 'homepage') {
          liveAnnouncements.announceGameState(STRINGS.status.game.returnedToHome);
        }
        break;
    }
  }
});

// Watch for connection status changes and announce them
watch(connectionStatus, (newStatus, oldStatus) => {
  if (oldStatus && newStatus !== oldStatus) {
    switch (newStatus) {
      case 'connecting':
        liveAnnouncements.announceProgress(STRINGS.status.connection.connectingToServer);
        break;
      case 'connected':
        liveAnnouncements.announceSuccess(STRINGS.status.connection.connectedToServer);
        break;
      case 'error':
        liveAnnouncements.announceError(STRINGS.status.connection.failedToConnect);
        break;
      case 'disconnected':
        // On the homepage the socket is intentionally torn down (#299), so a
        // "disconnected from server" announcement there would be misleading.
        // Only announce a disconnect that happens while actively matchmaking.
        if (currentState.value !== 'homepage') {
          liveAnnouncements.announceError(STRINGS.status.connection.disconnectedFromServer);
        }
        break;
    }
  }
});

onMounted(() => {
  setupKeyboardShortcuts();
  liveAnnouncements.createLiveRegions();

  if (props.autoStart) {
    startMatchmaking();
  }
});

// Setup keyboard shortcuts for matchmaking
const setupKeyboardShortcuts = () => {
  // Set context for matchmaking
  keyboardShortcuts.setContext('matchmaking');

  // Quick start game (Enter when on homepage with valid name)
  keyboardShortcuts.registerShortcut({
    id: 'quick-start-game',
    key: 'Enter',
    description: 'Start game when name is valid',
    context: 'matchmaking',
    allowInFormFields: true, // Allow Enter to work in the name input field
    handler: () => {
      if (currentState.value === 'homepage' && effectivePlayerName.value.length > 0) {
        startMatchmaking();
      }
    },
  });

  // Cancel search (Escape)
  keyboardShortcuts.registerShortcut({
    id: 'cancel-search',
    key: 'Escape',
    description: 'Cancel search',
    context: 'matchmaking',
    handler: () => {
      if (
        currentState.value === 'searching' ||
        currentState.value === 'extended-search' ||
        currentState.value === 'starting-bot'
      ) {
        cancelSearch();
      }
    },
  });

  // Retry connection (R when connection error)
  keyboardShortcuts.registerShortcut({
    id: 'retry-connection',
    key: 'r',
    description: 'Retry connection',
    context: 'matchmaking',
    handler: () => {
      if (currentState.value === 'connection-error') {
        retryConnection();
      }
    },
  });

  // Try again (T when timeout error)
  keyboardShortcuts.registerShortcut({
    id: 'try-again',
    key: 't',
    description: 'Try finding opponent again',
    context: 'matchmaking',
    handler: () => {
      if (currentState.value === 'timeout-error') {
        startMatchmaking();
      }
    },
  });

  // Go home (H when in error states)
  keyboardShortcuts.registerShortcut({
    id: 'go-home',
    key: 'h',
    description: 'Return to home screen',
    context: 'matchmaking',
    handler: () => {
      if (currentState.value === 'timeout-error' || currentState.value === 'connection-error') {
        goHome();
      }
    },
  });
};

// Cleanup is handled automatically by the composable
</script>

<style scoped>
/* ──────── Matchmaking redesign — Direction A "Quiet Pulse" ──────── */
.mm-stack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 26px;
  width: 100%;
  max-width: 360px;
  text-align: center;
}

.mm-headstack {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.mm-eyebrow {
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--color-arena-text-muted);
  margin: 0;
}
.mm-eyebrow.is-empty {
  color: var(--color-energy-warning);
}

.mm-title {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 24px;
  letter-spacing: 0.02em;
  line-height: 1.05;
  text-transform: uppercase;
  white-space: nowrap;
  color: var(--color-arena-text-bright);
  margin: 0;
}

.mm-status {
  font-family: var(--font-sans);
  font-size: 15px;
  line-height: 1.5;
  color: var(--color-arena-text-muted);
  margin: 0 auto;
  max-width: 280px;
  text-wrap: pretty;
}

/* radar ripple orb */
.mmA-pulse {
  position: relative;
  width: 132px;
  height: 132px;
  display: grid;
  place-items: center;
}
.mmA-pulse .ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1.5px solid var(--color-energy-primary);
  opacity: 0;
  animation: mmA-ripple 4s var(--ease-smooth) infinite;
}
.mmA-pulse .ring:nth-child(2) {
  animation-delay: 1.3s;
}
.mmA-pulse .ring:nth-child(3) {
  animation-delay: 2.6s;
}
.mmA-pulse .core {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 35%, #e8d08a, var(--color-energy-primary) 72%);
  box-shadow:
    0 0 28px rgba(201, 168, 76, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
  display: grid;
  place-items: center;
  color: #2a2010;
  animation: mmA-breathe 4s ease-in-out infinite;
}
@keyframes mmA-ripple {
  0% {
    transform: scale(0.42);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}
@keyframes mmA-breathe {
  0%,
  100% {
    transform: scale(1);
    box-shadow:
      0 0 24px rgba(201, 168, 76, 0.4),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
  50% {
    transform: scale(1.06);
    box-shadow:
      0 0 42px rgba(201, 168, 76, 0.65),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
  }
}
/* failure: cold, still, grey */
.mmA-pulse.is-empty .ring {
  display: none;
}
.mmA-pulse.is-empty .core {
  background: radial-gradient(circle at 50% 35%, #4a4438, #2a251d 72%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 2px 10px rgba(0, 0, 0, 0.5);
  color: var(--color-arena-text-dim);
  animation: none;
}

/* #264 · match found — the "found" orb locks in green & confident */
.mmA-pulse.is-found .ring {
  border-color: var(--color-matchmaking-found);
}
.mmA-pulse.is-found .core {
  background: radial-gradient(circle at 50% 35%, #74e8c6, var(--color-matchmaking-found) 74%);
  box-shadow:
    0 0 30px rgba(0, 196, 143, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  color: #06382a;
  animation:
    mmA-found-in var(--animate-duration-slow) var(--ease-bounce) both,
    mmA-breathe-green 3s ease-in-out 0.5s infinite;
}
@keyframes mmA-found-in {
  0% {
    transform: scale(0.4);
    opacity: 0;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}
@keyframes mmA-breathe-green {
  0%,
  100% {
    box-shadow:
      0 0 24px rgba(0, 196, 143, 0.45),
      inset 0 1px 0 rgba(255, 255, 255, 0.35);
  }
  50% {
    box-shadow:
      0 0 42px rgba(0, 196, 143, 0.7),
      inset 0 1px 0 rgba(255, 255, 255, 0.35);
  }
}
.mm-eyebrow.is-found {
  color: var(--color-matchmaking-found);
}

/* #264 · connection lost — cold, offline orb (cf. .is-empty) */
.mmA-pulse.is-error .ring {
  display: none;
}
.mmA-pulse.is-error .core {
  background: radial-gradient(circle at 50% 35%, #4a4438, #2a251d 72%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 2px 10px rgba(0, 0, 0, 0.5),
    0 0 18px rgba(239, 68, 68, 0.14);
  color: var(--color-energy-danger);
  animation: none;
}
.mm-eyebrow.is-error {
  color: var(--color-energy-danger);
}
/* "searching for signal" variant — two slate rings, offset half a cycle
   (delay 1.4s = 2.8s / 2), emit a steady ping every 1.4s for the live retry.
   The third ring is dropped so the radar stays slow. */
.mmA-pulse.is-error.is-reconnecting .ring {
  display: block;
  border-color: var(--color-energy-secondary);
  border-width: 1.5px;
  animation: mmA-ripple 2.8s var(--ease-smooth) infinite;
}
.mmA-pulse.is-error.is-reconnecting .ring:nth-child(2) {
  animation-delay: 1.4s;
}
.mmA-pulse.is-error.is-reconnecting .ring:nth-child(3) {
  display: none;
}

/* full-width stacked action buttons — labels never wrap */
.mm-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  max-width: 320px;
  margin: 4px auto 0;
}
.mm-btn {
  width: 100%;
  min-height: 52px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: var(--radius-button);
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: 16px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  border: none;
  cursor: pointer;
  transition:
    transform 300ms var(--ease-smooth),
    filter 300ms var(--ease-smooth),
    border-color 300ms ease,
    color 300ms ease;
}
.mm-btn--primary {
  background: linear-gradient(180deg, var(--color-primary-500), var(--color-primary-600));
  color: var(--color-arena-surface);
  box-shadow: 0 2px 12px rgba(201, 168, 76, 0.25);
}
.mm-btn--primary:hover {
  transform: translateY(-1px);
  filter: brightness(1.08);
}
.mm-btn--primary:active {
  transform: translateY(0) scale(0.98);
}
.mm-btn--secondary {
  background: transparent;
  color: var(--color-arena-text-bright);
  border: 2px solid var(--color-arena-border);
}
.mm-btn--secondary:hover {
  transform: translateY(-1px);
  border-color: var(--color-energy-primary);
}
.mm-btn--secondary:active {
  transform: translateY(0) scale(0.98);
}
.mm-btn--ghost {
  background: transparent;
  border: none;
  min-height: 44px;
  color: var(--color-arena-text-muted);
  font-size: 13px;
  letter-spacing: 0.12em;
}
.mm-btn--ghost:hover {
  color: var(--color-arena-text-bright);
}
.mm-btn .mm-btn-icon {
  width: 17px;
  height: 17px;
  display: inline-block;
}
.mm-btn:focus-visible {
  outline: 3px solid var(--color-primary-600);
  outline-offset: 2px;
  box-shadow:
    0 0 0 1px #ffffff,
    0 0 0 4px var(--color-primary-500);
}

/* disabled / busy action (e.g. CONNECTING…) */
.mm-btn:disabled {
  cursor: default;
}
.mm-btn--primary:disabled {
  transform: none;
  filter: none;
  opacity: 0.85;
  animation: mm-btn-busy 1.6s ease-in-out infinite;
}
@keyframes mm-btn-busy {
  0%,
  100% {
    opacity: 0.6;
  }
  50% {
    opacity: 0.95;
  }
}

@media (prefers-reduced-motion: reduce) {
  .mmA-pulse .ring,
  .mmA-pulse .core,
  .mm-btn--primary:disabled {
    animation: none !important;
  }
  .mm-btn:hover,
  .mm-btn:active {
    transform: none;
  }
}

/* ──────── Home / lobby redesign ────────
   One flex column that fits the viewport — masthead + arena card centered as a
   single unit so the screen never scrolls on mobile. Brought in line with the
   gameplay / results / matchmaking arena-card system; reuses the .mm-btn action
   buttons above. */
.hm-col {
  position: relative;
  flex: 1;
  /* Cap at the viewport, don't just floor it: the parent matchmaking-container
     is min-h-screen with no height cap, so a bare min-height here would let the
     column grow past the screen (and scroll the document) whenever the content
     is taller than the viewport. max-height bounds it so the .hm-center
     overflow-y fallback below can actually engage instead. */
  min-height: 100dvh;
  max-height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 56px 22px 30px;
}

.hm-center {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 42px;
  text-align: center;
  /* fallback only: the page never scrolls, but on very small screens the
     unverified banner can overflow — let the centered region scroll so the
     resend button stays reachable. */
  overflow-y: auto;
}

/* masthead */
.hm-mast {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  animation: hm-rise 0.5s var(--ease-dramatic) both;
}
.hm-wordmark {
  margin: 0;
  font-weight: 800;
  /* 40px floor (mock used 44) so the wordmark still fits one line at 320px once
     the nested matchmaking-container padding is accounted for */
  font-size: clamp(40px, 13vw, 58px);
  line-height: 0.92;
  /* tight tracking per the mock — the wordmark must fit one line inside the
     nested matchmaking-container padding without clipping on narrow phones */
  letter-spacing: -0.01em;
  color: var(--color-arena-text-bright);
  text-shadow: 0 2px 12px rgba(201, 168, 76, 0.3);
}
.hm-tagline {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--color-arena-text-muted);
}
.hm-vs {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 244px;
  max-width: 70vw;
}
.hm-vs-line {
  flex: 1;
  height: 1px;
}
.hm-vs-line--l {
  background: linear-gradient(90deg, transparent, var(--color-energy-primary));
}
.hm-vs-line--r {
  background: linear-gradient(90deg, var(--color-energy-primary), transparent);
}
.hm-vs-text {
  font-weight: 800;
  font-size: 18px;
  letter-spacing: 0.15em;
  color: var(--color-energy-primary);
  transform: rotate(-6deg);
}

/* arena card */
.hm-card {
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  background: var(--color-arena-surface);
  border: 1px solid var(--color-arena-border);
  border-radius: 16px;
  box-shadow: var(--shadow-card);
  animation: hm-rise 0.5s var(--ease-dramatic) 0.1s both;
}

/* player identity row */
.hm-player {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}
.hm-avatar {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 20px;
  color: var(--color-arena-text-bright);
  background: rgba(26, 22, 18, 0.8);
  border: 1.5px solid var(--color-energy-primary);
  border-radius: 10px;
  /* letterpress tile (production has no --shadow-tile token) */
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 2px 6px rgba(0, 0, 0, 0.4);
}
.hm-player-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
}
.hm-name {
  margin: 0;
  max-width: 200px;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-arena-text-bright);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.hm-player-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--color-arena-text-muted);
}
.hm-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-energy-success);
  box-shadow: 0 0 8px rgba(0, 196, 143, 0.6);
}
.hm-dot--warn {
  background: var(--color-energy-warning);
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.6);
}

.hm-divider {
  height: 1px;
  background: var(--color-arena-border);
}

.hm-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
/* disabled (unverified) PLAY NOW reads as inert, not the .mm-btn busy shimmer */
.hm-actions .mm-btn--primary:disabled {
  animation: none;
  opacity: 0.5;
}
.hm-bot-note {
  margin: 0;
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--color-arena-text-muted);
  text-align: center;
}

@keyframes hm-rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* desktop — same card, wordmark larger, actions side-by-side */
@media (min-width: 1024px) {
  .hm-wordmark {
    font-size: 76px;
  }
  .hm-card {
    max-width: 440px;
    padding: 22px;
  }
  .hm-actions {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .hm-actions .mm-btn {
    flex: 1;
  }
  .hm-bot-note {
    flex-basis: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hm-mast,
  .hm-card {
    animation: none;
  }
}
</style>
